import { Injectable } from '@angular/core';

export interface PdfDocumentField {
  label: string;
  value: string;
}

export interface PdfDocumentTable {
  headers: string[];
  rows: string[][];
}

export interface PdfDocumentPayload {
  documentType: string;
  documentNumber: string;
  title?: string;
  subtitle?: string;
  fields?: PdfDocumentField[];
  table?: PdfDocumentTable;
  totals?: PdfDocumentField[];
  notes?: string;
  fileName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentPdfService {
  base64(payload: PdfDocumentPayload): string {
    const bytes = new TextEncoder().encode(this.buildPdf(this.buildLines(payload)));
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary);
  }

  download(payload: PdfDocumentPayload): void {
    const lines = this.buildLines(payload);
    const pdfContent = this.buildPdf(lines);
    const fileName = this.sanitizeFileName(payload.fileName || `${payload.documentType}-${payload.documentNumber}.pdf`);

    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private buildLines(payload: PdfDocumentPayload): string[] {
    const lines: string[] = [];
    const header = payload.title?.trim().length ? payload.title.trim() : payload.documentType.trim();
    const subtitle = payload.subtitle?.trim().length
      ? payload.subtitle.trim()
      : `${payload.documentType.trim()} ${payload.documentNumber.trim()}`;

    lines.push(header);
    lines.push(subtitle);
    lines.push(`Generated: ${new Date().toLocaleString()}`);
    lines.push('');

    if ((payload.fields ?? []).length > 0) {
      lines.push('Details');
      for (const field of payload.fields ?? []) {
        lines.push(`${field.label}: ${field.value}`);
      }
      lines.push('');
    }

    if (payload.table && payload.table.headers.length > 0) {
      const normalizedRows = payload.table.rows ?? [];
      lines.push('Lines');
      lines.push(payload.table.headers.join(' | '));
      lines.push('-'.repeat(Math.max(40, payload.table.headers.join(' | ').length)));

      for (const row of normalizedRows) {
        lines.push(row.join(' | '));
      }

      lines.push('');
    }

    if ((payload.totals ?? []).length > 0) {
      lines.push('Totals');
      for (const total of payload.totals ?? []) {
        lines.push(`${total.label}: ${total.value}`);
      }
      lines.push('');
    }

    if (payload.notes?.trim().length) {
      lines.push('Notes');
      lines.push(payload.notes.trim());
      lines.push('');
    }

    return lines
      .flatMap((line) => this.wrapLine(line, 96))
      .map((line) => this.normalizeAscii(line));
  }

  private buildPdf(lines: string[]): string {
    const linesPerPage = 48;
    const pageChunks: string[][] = [];

    for (let i = 0; i < lines.length; i += linesPerPage) {
      pageChunks.push(lines.slice(i, i + linesPerPage));
    }

    const objects: string[] = [];
    const addObject = (body: string): number => {
      objects.push(body);
      return objects.length;
    };

    const pagesId = addObject('');
    const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
    const pageIds: number[] = [];

    for (const pageLines of pageChunks.length > 0 ? pageChunks : [[]]) {
      const stream = this.buildPageTextStream(pageLines);
      const streamLength = new TextEncoder().encode(stream).length;
      const contentId = addObject(`<< /Length ${streamLength} >>\nstream\n${stream}\nendstream`);
      const pageId = addObject(
        `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`,
      );
      pageIds.push(pageId);
    }

    objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

    const catalogId = addObject(`<< /Type /Catalog /Pages ${pagesId} 0 R >>`);

    let pdf = '%PDF-1.4\n';
    const offsets: number[] = [0];

    for (let i = 0; i < objects.length; i += 1) {
      offsets.push(new TextEncoder().encode(pdf).length);
      pdf += `${i + 1} 0 obj\n${objects[i]}\nendobj\n`;
    }

    const xrefOffset = new TextEncoder().encode(pdf).length;
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';

    for (let i = 1; i < offsets.length; i += 1) {
      pdf += `${offsets[i].toString().padStart(10, '0')} 00000 n \n`;
    }

    pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\n`;
    pdf += `startxref\n${xrefOffset}\n%%EOF`;

    return pdf;
  }

  private buildPageTextStream(lines: string[]): string {
    const commands: string[] = ['BT', '/F1 10 Tf', '40 760 Td'];

    lines.forEach((line, index) => {
      if (index > 0) {
        commands.push('0 -14 Td');
      }

      commands.push(`(${this.escapePdfText(line)}) Tj`);
    });

    commands.push('ET');
    return commands.join('\n');
  }

  private wrapLine(input: string, maxLength: number): string[] {
    const line = input ?? '';
    if (line.length <= maxLength) {
      return [line];
    }

    const words = line.split(/\s+/).filter((word) => word.length > 0);
    const output: string[] = [];
    let current = '';

    for (const word of words) {
      const next = current.length > 0 ? `${current} ${word}` : word;
      if (next.length <= maxLength) {
        current = next;
        continue;
      }

      if (current.length > 0) {
        output.push(current);
      }

      current = word;
    }

    if (current.length > 0) {
      output.push(current);
    }

    return output.length > 0 ? output : [''];
  }

  private escapePdfText(value: string): string {
    return value
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)')
      .replace(/\r/g, ' ')
      .replace(/\n/g, ' ');
  }

  private normalizeAscii(value: string): string {
    return value.replace(/[^\x20-\x7E]/g, '?');
  }

  private sanitizeFileName(value: string): string {
    const normalized = value.trim().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9._-]/g, '').toLowerCase();
    return normalized.endsWith('.pdf') ? normalized : `${normalized}.pdf`;
  }
}
